import { cva, type VariantProps } from "class-variance-authority"
import { Loader2, X } from "lucide-react"
import * as React from "react"

import { useFieldProps } from "@/components/ui/form-field"
import { cn } from "@/lib/utils"

/*
   The border and the focus ring live on a WRAPPER, not on the <input>.
   That is what makes prefixes and suffixes possible without two competing
   focus treatments — the inner input drops its own outline and the wrapper
   picks it up via `has-[input:focus-visible]`.
*/

const inputWrapper = cva(
  [
    "group/input relative flex w-full items-center gap-2",
    "rounded-md border bg-surface",
    "transition-colors duration-120 ease-out",
    // the ring lands on the wrapper, so affixes sit inside it
    "has-[input:focus-visible]:outline has-[input:focus-visible]:outline-2",
    "has-[input:focus-visible]:outline-offset-2",
    "has-[input:focus-visible]:outline-[color:var(--brand)]",
    "has-[input:disabled]:cursor-not-allowed has-[input:disabled]:bg-surface-sunken",
  ],
  {
    variants: {
      inputSize: {
        sm: "h-7.5 px-2.5 text-body-sm [&_svg]:size-3.5",
        md: "h-9 px-3 text-body [&_svg]:size-4",
        lg: "h-10.5 px-3.5 text-body [&_svg]:size-4",
      },
      invalid: {
        true: "border-err bg-err-tint/40",
        false: "border-border hover:border-border-strong",
      },
    },
    defaultVariants: { inputSize: "md", invalid: false },
  },
)

export interface InputProps
  extends Omit<React.ComponentPropsWithoutRef<"input">, "size" | "prefix">,
    VariantProps<typeof inputWrapper> {
  /**
   * Render in IBM Plex Mono with tabular figures. Use for anything a person
   * reads character by character: registration numbers, rule ids, stamp
   * dates, API keys. Not for names, notes or search.
   */
  mono?: boolean
  /**
   * Sits inside the border, before the input. An icon, or a fixed string
   * the person shouldn't have to type ("EPA", "$").
   */
  prefix?: React.ReactNode
  /** Sits inside the border, after the input. A unit, a count, an icon. */
  suffix?: React.ReactNode
  /**
   * Shows a spinner in the suffix slot. For async validation — resolving a
   * registration number against PPLS, checking an email is free.
   */
  loading?: boolean
  /**
   * Renders a clear button when the field has a value. For search and
   * filter inputs; not for form fields, where clearing is destructive
   * and the person can select-all instead.
   */
  clearable?: boolean
  /** Called when the clear button is pressed. Required if `clearable`. */
  onClear?: () => void
  /** Class for the wrapper. `className` targets the <input> itself. */
  wrapperClassName?: string
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    className,
    wrapperClassName,
    inputSize,
    invalid,
    mono = false,
    prefix,
    suffix,
    loading = false,
    clearable = false,
    onClear,
    type = "text",
    value,
    ...rest
  },
  ref,
) {
  // Pull id / aria-describedby / aria-invalid / required / disabled from
  // the surrounding FormField. Explicit props still win.
  const props = useFieldProps({ value, ...rest } as typeof rest & {
    value?: React.ComponentPropsWithoutRef<"input">["value"]
  })

  const isInvalid =
    invalid ?? (props["aria-invalid"] === true || props["aria-invalid"] === "true")

  const hasValue = props.value !== undefined && props.value !== null && props.value !== ""
  const showClear = clearable && hasValue && !props.disabled && !loading

  return (
    <div
      data-slot="input-wrapper"
      className={cn(inputWrapper({ inputSize, invalid: Boolean(isInvalid) }), wrapperClassName)}
    >
      {prefix && (
        <span aria-hidden="true" className="shrink-0 text-text-3 select-none">
          {prefix}
        </span>
      )}

      <input
        ref={ref}
        type={type}
        className={cn(
          // the wrapper owns the border and the ring
          "min-w-0 flex-1 bg-transparent outline-none",
          "text-text placeholder:text-text-disabled",
          "disabled:cursor-not-allowed disabled:text-text-disabled",
          // date/time inputs render a native picker glyph; tone it down
          "[&::-webkit-calendar-picker-indicator]:opacity-50",
          "[&::-webkit-calendar-picker-indicator]:hover:opacity-100",
          "[&::-webkit-calendar-picker-indicator]:cursor-pointer",
          mono && "font-id tabular-nums",
          className,
        )}
        {...props}
      />

      {showClear && (
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear"
          className={cn(
            "grid size-5 shrink-0 place-items-center rounded-sm",
            "text-text-3 transition-colors duration-120 ease-out",
            "hover:bg-surface-sunken hover:text-text",
          )}
        >
          <X className="size-3.5" aria-hidden="true" />
        </button>
      )}

      {loading && (
        <Loader2
          className="shrink-0 animate-spin text-text-3 motion-reduce:animate-none"
          aria-label="Checking"
        />
      )}

      {suffix && !loading && (
        <span aria-hidden="true" className="shrink-0 text-text-3 select-none">
          {suffix}
        </span>
      )}
    </div>
  )
})

/*
   InputAffix — a fixed, non-editable segment joined to an input.
   Different from `prefix`: an affix sits OUTSIDE the input's border on its
   own tinted surface, for a value that is structurally part of the field
   but never typed — the "www." of a URL, the fixed company portion of a
   distributor registration number.
*/

export interface InputAffixProps extends React.ComponentPropsWithoutRef<"span"> {
  side?: "start" | "end"
}

export const InputAffix = React.forwardRef<HTMLSpanElement, InputAffixProps>(function InputAffix(
  { className, side = "start", ...props },
  ref,
) {
  return (
    <span
      ref={ref}
      className={cn(
        "inline-flex h-8.5 shrink-0 items-center border bg-surface-sunken px-3",
        "font-id text-body text-text-3 select-none",
        side === "start" ? "rounded-l-md border-r-0" : "rounded-r-md border-l-0",
        className,
      )}
      {...props}
    />
  )
})
